import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Alert from '@mui/material/Alert';
import config from '../config';
import { useRules } from '../context/RuleContext';

function getActionColor(action) {
  if (action.includes('ADD') || action.includes('INSERT')) return 'success';
  if (action.includes('DELETE')) return 'error';
  if (action.includes('UPDATE') || action.includes('EXTEND') || action.includes('BULK')) return 'warning';
  if (action.includes('UPLOAD') || action.includes('SYNC') || action.includes('FETCH')) return 'info';
  if (action.includes('DOWNLOAD')) return 'primary';
  if (action.includes('APPROVE')) return 'success';
  if (action.includes('REJECT')) return 'error';
  return 'default';
}

export default function ChangeLog() {
  const { changeLog } = useRules();
  const [tabValue, setTabValue] = useState(0);
  const [serverLogs, setServerLogs] = useState([]);

  // Fetch logs from backend
  useEffect(() => {
    fetch(`${config.API_BASE}/audit/logs`)
      .then(r => r.json())
      .then(data => { if (Array.isArray(data)) setServerLogs(data); })
      .catch(() => {});
  }, []);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>Change Log</Typography>

      <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ mb: 2 }}>
        <Tab label={`Local Session (${changeLog.length})`} />
        <Tab label={`Server Audit Log (${serverLogs.length})`} />
      </Tabs>

      {/* Local Session Logs */}
      {tabValue === 0 && (
        <Card>
          {changeLog.length === 0 ? (
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No changes in this session yet.</Typography>
            </CardContent>
          ) : (
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {changeLog.map(entry => (
                    <TableRow key={entry.id} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        <Typography variant="caption">{new Date(entry.timestamp).toLocaleString()}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={entry.action} size="small" color={getActionColor(entry.action)} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{entry.details}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      {/* Server Audit Logs */}
      {tabValue === 1 && (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            These logs are from the MongoDB server — they persist across sessions and show all users' activity.
          </Alert>
          <Card>
            {serverLogs.length === 0 ? (
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">No server logs. Backend may be offline.</Typography>
              </CardContent>
            ) : (
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {serverLogs.map(entry => (
                      <TableRow key={entry.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Typography variant="caption">
                            {entry.timestamp ? new Date(entry.timestamp).toLocaleString() : '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={entry.username || 'system'} size="small" variant="outlined"
                            color={entry.username === 'admin' ? 'error' : 'primary'} />
                        </TableCell>
                        <TableCell>
                          <Chip label={entry.action} size="small" color={getActionColor(entry.action)} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{entry.details}</Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Card>
        </Box>
      )}
    </Box>
  );
}
